// 页 3：Review 工作台
// 待审候选列表 + 图候选挑选区 + listing/价审 + 审计流

import React, { useState, useMemo } from 'react';
import { Card, Row, Col, List, Avatar, Tag, Empty, Modal, Select, Input, Button, Divider, Space, message } from 'antd';
import { Tabs } from '../AntdCompat';
import { CheckOutlined, CloseOutlined } from '@ant-design/icons';
import { ImageCandidateGrid, PriceTierCard, DecisionAuditTimeline } from '../components';
import {
  aiCandidates, STATUS_LABELS, STATUS_COLORS, REJECT_REASON_LABELS,
  getQualitiesByCandidate, getDraftsByCandidate, getPriceRecByCandidate, getDecisionsByCandidate,
} from '../data';
import { RejectReasonCode } from '../types';

const PENDING_STATUSES = ['IMAGE_REVIEW', 'LISTING_DRAFT', 'PRICED', 'SHOPIFY_DRAFT'] as const;

const ReviewWorkstation: React.FC = () => {
  const pendingList = useMemo(
    () => aiCandidates.filter(c => (PENDING_STATUSES as readonly string[]).includes(c.status)),
    []
  );
  const [selectedId, setSelectedId] = useState<string | null>(pendingList[0]?.candidate_id || null);
  const [rejectModalOpen, setRejectModalOpen] = useState(false);
  const [rejectImageId, setRejectImageId] = useState<string | null>(null);
  const [rejectReason, setRejectReason] = useState<RejectReasonCode>('BLURRY');
  const [rejectText, setRejectText] = useState('');

  const current = aiCandidates.find(c => c.candidate_id === selectedId);
  const qualities = current ? getQualitiesByCandidate(current.candidate_id) : [];
  const drafts = current ? getDraftsByCandidate(current.candidate_id) : [];
  const priceRec = current ? getPriceRecByCandidate(current.candidate_id) : undefined;
  const decisions = current ? getDecisionsByCandidate(current.candidate_id) : [];

  const handleSelectImage = (imageId: string) => {
    message.success('已选定该图（mock 写 ai_candidate_quality.is_selected=true）');
  };

  const handleRejectImage = (imageId: string) => {
    setRejectImageId(imageId);
    setRejectModalOpen(true);
  };

  const submitReject = () => {
    message.success(`已拒（写 ai_candidate_quality.reject_tags=[${rejectReason}] + reject_reason_text）`);
    setRejectModalOpen(false);
    setRejectText('');
  };

  return (
    <div style={{ padding: '16px 24px' }}>
      <Row gutter={12}>
        {/* 左侧：待审候选列表 */}
        <Col span={6}>
          <Card title={`待审候选 (${pendingList.length})`} size="small" style={{ height: 'calc(100vh - 140px)' }}>
            <List
              dataSource={pendingList}
              renderItem={c => (
                <List.Item
                  onClick={() => setSelectedId(c.candidate_id)}
                  style={{
                    cursor: 'pointer',
                    padding: 12,
                    background: selectedId === c.candidate_id ? '#e6f7ff' : undefined,
                    borderLeft: selectedId === c.candidate_id ? '3px solid #1890ff' : '3px solid transparent',
                  }}
                >
                  <List.Item.Meta
                    avatar={<Avatar src={c.thumbnail_url} size={48} shape="square" />}
                    title={<span style={{ fontSize: 13 }}>{c.title}</span>}
                    description={
                      <>
                        <Tag color={STATUS_COLORS[c.status]}>{STATUS_LABELS[c.status]}</Tag>
                        <span style={{ fontSize: 10, color: '#8c8c8c' }}>{c.owner_name}</span>
                      </>
                    }
                  />
                </List.Item>
              )}
            />
          </Card>
        </Col>

        {/* 中间：图候选 + listing + 价 */}
        <Col span={12}>
          {!current ? <Empty description="请从左侧选择一个候选" /> : (
            <Card
              title={
                <Space>
                  <span>{current.title}</span>
                  <Tag color={STATUS_COLORS[current.status]}>{STATUS_LABELS[current.status]}</Tag>
                </Space>
              }
              extra={
                <Space>
                  <Button>编辑文案</Button>
                  <Button>改价</Button>
                  <Button danger>拒绝该候选</Button>
                  <Button type="primary" icon={<CheckOutlined />}>一键 publish</Button>
                </Space>
              }
              size="small"
              style={{ height: 'calc(100vh - 140px)', overflow: 'auto' }}
            >
              <Tabs
                items={[
                  {
                    key: 'image',
                    label: `图候选 (${qualities.filter(q => !q.cv_filtered).length}/${qualities.length})`,
                    children: <ImageCandidateGrid qualities={qualities} onSelect={handleSelectImage} onReject={handleRejectImage} />,
                  },
                  {
                    key: 'listing',
                    label: `Listing 草稿 (${drafts.length})`,
                    children: drafts.length === 0 ? <Empty description="尚未生成 listing" /> : (
                      <div>
                        {drafts.slice(0, 1).map(d => (
                          <Card key={d.id} size="small" title={`最新版 v${d.version_no}`} extra={<Button type="link" size="small">查看历史</Button>}>
                            <div style={{ marginBottom: 12 }}>
                              <div style={{ fontSize: 12, color: '#8c8c8c' }}>title</div>
                              <Input value={d.title} />
                            </div>
                            <div style={{ marginBottom: 12 }}>
                              <div style={{ fontSize: 12, color: '#8c8c8c' }}>handle</div>
                              <Input value={d.handle} />
                            </div>
                            <Row gutter={12}>
                              <Col span={12}>
                                <div style={{ fontSize: 12, color: '#8c8c8c' }}>seo_title</div>
                                <Input value={d.seo_title} />
                              </Col>
                              <Col span={12}>
                                <div style={{ fontSize: 12, color: '#8c8c8c' }}>seo_desc</div>
                                <Input value={d.seo_desc} />
                              </Col>
                            </Row>
                            <div style={{ marginTop: 12 }}>
                              <div style={{ fontSize: 12, color: '#8c8c8c' }}>description_html</div>
                              <Input.TextArea value={d.description_html} rows={6} />
                            </div>
                            <div style={{ marginTop: 12 }}>
                              <div style={{ fontSize: 12, color: '#8c8c8c' }}>tags</div>
                              {d.tags.map(t => <Tag key={t} closable>{t}</Tag>)}
                            </div>
                            <div style={{ marginTop: 12 }}>
                              <div style={{ fontSize: 12, color: '#8c8c8c' }}>collections</div>
                              {d.collections.map(c => <Tag key={c} color="cyan" closable>{c}</Tag>)}
                            </div>
                          </Card>
                        ))}
                      </div>
                    ),
                  },
                  {
                    key: 'price',
                    label: '三档价',
                    children: priceRec ? <PriceTierCard rec={priceRec} /> : <Empty description="尚未生成定价建议" />,
                  },
                ]}
              />
            </Card>
          )}
        </Col>

        {/* 右侧：审计流 */}
        <Col span={6}>
          <Card title="审计流（ai_candidate_decision）" size="small" style={{ height: 'calc(100vh - 140px)', overflow: 'auto' }}>
            {current ? <DecisionAuditTimeline decisions={decisions} /> : <Empty />}
          </Card>
        </Col>
      </Row>

      {/* 拒图原因 modal */}
      <Modal
        title="选择拒图原因（结构化采集）"
        open={rejectModalOpen}
        onCancel={() => setRejectModalOpen(false)}
        onOk={submitReject}
        okText="提交拒绝"
        okButtonProps={{ danger: true }}
      >
        <div style={{ marginBottom: 12 }}>
          <div style={{ fontSize: 12, color: '#8c8c8c', marginBottom: 4 }}>原因码（必填，9 选 1）</div>
          <Select value={rejectReason} onChange={setRejectReason} style={{ width: '100%' }}>
            {Object.keys(REJECT_REASON_LABELS).map(k => (
              <Select.Option key={k} value={k}>{k} — {REJECT_REASON_LABELS[k as RejectReasonCode]}</Select.Option>
            ))}
          </Select>
        </div>
        <div>
          <div style={{ fontSize: 12, color: '#8c8c8c', marginBottom: 4 }}>说明（选填，写给 AI 看的）</div>
          <Input.TextArea
            rows={3}
            value={rejectText}
            onChange={e => setRejectText(e.target.value)}
            placeholder="例如：戒指爪镶位置不对，AI 把侧面爪渲染成 4 爪了，原款是 6 爪"
          />
        </div>
        <Divider plain>反馈链路</Divider>
        <div style={{ fontSize: 12, color: '#595959' }}>
          ⚠️ <strong>这条拒图原因不反馈给 prompt</strong>（扩散模型抽卡范式下没用），
          而是反馈给两个上游：<br />
          • <strong>prompt 模板分品类维护</strong>：耳钉若拒率 80% → 单独调耳钉模板<br />
          • <strong>选款环节</strong>：某品类抽满 15 张仍 0 通过 → 这类款"AI 生图难"降权
        </div>
      </Modal>
    </div>
  );
};

export default ReviewWorkstation;
